"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FileText } from "lucide-react";
import type { QuickBooksInvoice } from "@/lib/types/quickbooks-unified";
import { formatCurrency } from "../utils/invoiceFormatters";

interface InvoiceLineItemsTabProps {
  invoice?: QuickBooksInvoice;
}

/**
 * Line Items tab component for invoice details
 * Displays invoice line items in an expandable accordion format
 */
export const InvoiceLineItemsTab: React.FC<InvoiceLineItemsTabProps> = ({ invoice }) => {
  if (!invoice?.Line || invoice.Line.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-10">
          <FileText className="h-10 w-10 text-gray-400 mx-auto mb-3" />
          <p className="text-sm text-gray-600">No line items found for this invoice.</p>
        </CardContent>
      </Card>
    );
  }

  const lineItems = invoice.Line.filter((line) => line.DetailType === "SalesItemLineDetail");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span>Line Items</span>
          <Badge variant="outline" className="text-xs">
            {lineItems.length} item{lineItems.length !== 1 ? "s" : ""}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Accordion type="single" collapsible className="w-full">
          {lineItems.map((line, index) => (
            <AccordionItem
              key={line.Id || index}
              value={`item-${index}`}
              className="border rounded-lg mb-4"
            >
              <AccordionTrigger className="px-4 py-4 hover:no-underline">
                <div className="flex justify-between items-center w-full pr-4">
                  <div className="flex-1 text-left">
                    <div className="font-medium text-lg">
                      {line.SalesItemLineDetail?.ItemRef?.name || "Item"}
                    </div>
                    <div className="text-sm text-gray-600 font-normal">
                      Line {line.LineNum || index + 1}
                    </div>
                  </div>
                  <div className="text-lg font-semibold text-green-600">
                    {formatCurrency(line.Amount)}
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-4">
                  {/* `DescriptionLineDetail` is QBO's marker object for a
                      description-only line; the text itself lives on the line's
                      `Description`. Rendering the object was a React "Objects are
                      not valid as a React child" crash waiting on any invoice
                      that had such a line. */}
                  {line.DescriptionLineDetail && (
                    <div className="p-4 bg-card-50 rounded-lg">
                      <div className="text-sm font-medium text-gray-600 mb-2">Description:</div>
                      <div className="text-base leading-relaxed">{line.Description}</div>
                    </div>
                  )}

                  {line.SalesItemLineDetail && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <div className="text-sm font-medium text-gray-600 mb-2">Quantity</div>
                        <div className="text-lg font-semibold">
                          {line.SalesItemLineDetail.Qty || "N/A"}
                        </div>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg">
                        <div className="text-sm font-medium text-gray-600 mb-2">Unit Price</div>
                        <div className="text-lg font-semibold">
                          {formatCurrency(line.SalesItemLineDetail.UnitPrice)}
                        </div>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <div className="text-sm font-medium text-gray-600 mb-2">Account</div>
                        <div className="text-lg font-semibold">
                          {line.SalesItemLineDetail.ItemAccountRef?.name || "N/A"}
                        </div>
                      </div>
                    </div>
                  )}

                  {line.SalesItemLineDetail?.TaxCodeRef && (
                    <div className="p-4 bg-yellow-50 rounded-lg">
                      <div className="text-sm font-medium text-gray-600 mb-2">Tax Code</div>
                      <div className="text-base font-semibold">
                        {line.SalesItemLineDetail.TaxCodeRef.value}
                      </div>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </CardContent>
    </Card>
  );
};
